import type React from "react";

import type { IconName } from "@fortawesome/pro-regular-svg-icons";
import clsx from "clsx";
import type { RuleSet } from "styled-components";
import styled, { css } from "styled-components";

import { Spinner } from "../Spinner";

import { getIcon } from "./getIcon";

type Variant = "danger" | "primary" | "secondary" | "tertiary" | "tertiaryBlue" | "text" | "action" | "secondaryLink";
export type Size = "large" | "small";

export interface BaseButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  children?: React.ReactNode;
  dataTestID?: string;
  fullWidth?: boolean;
  icon?: IconName | "bankid" | "circle-plus-regular" | "thumbtack-solid";
  iconPlacement?: "start" | "end";
  loading?: boolean;
  size?: Size;
  small?: boolean;
  square?: boolean;
  style?: Pick<
    React.CSSProperties,
    | "alignSelf"
    | "color"
    | "flexBasis"
    | "flexGrow"
    | "margin"
    | "marginBottom"
    | "marginLeft"
    | "marginTop"
    | "minHeight"
    | "maxHeight"
    | "padding"
  >;
  text?: string;
  title?: string;
  uppercase?: boolean;
  variant?: Variant;
}

// TODO Remove small prop
// TODO Fix text transform for size prop
export const BaseButton: React.FC<BaseButtonProps> = ({
  active = false,
  children,
  className,
  dataTestID,
  disabled,
  fullWidth,
  icon,
  iconPlacement = "start",
  loading,
  size,
  small = false,
  square = false,
  text,
  title,
  type = "button",
  uppercase = false,
  variant = "primary",
  ...props
}) => {
  const iconComponent = getIcon(icon, size);

  return (
    <StyledButton
      $active={active}
      className={clsx({ square }, className)}
      data-testid={dataTestID}
      disabled={disabled}
      title={title}
      type={type}
      $small={small}
      $size={size}
      $uppercase={uppercase}
      $variant={variant}
      $fullWidth={fullWidth}
      {...props}
    >
      {iconPlacement === "start" && iconComponent}
      {text}
      {children}
      {iconPlacement === "end" && iconComponent}
      {loading && <Spinner style={{ height: "30px", width: "30px" }} />}
    </StyledButton>
  );
};

const baseStyles = css`
  border-radius: 32px;
  min-height: 50px;
  max-height: 50px;
  flex-shrink: 0;
  flex-grow: 1;
  flex-basis: 176px;
  padding: 0 40px;
`;

export const getActionButtonColorStyles = (important = false, $activeClass?: string) => {
  const imp = important ? " !important" : "";
  return css`
    transition: 0.2s;
    color: ${({ theme }) => theme.colors.redesign.db100}${imp};
    border: 1px solid ${({ theme }) => theme.colors.redesign.db20}${imp};
    background: ${({ theme }) => theme.colors.white}${imp};

    &:focus {
      color: ${({ theme }) => theme.colors.redesign.db100}${imp};
      border-color: ${({ theme }) => theme.colors.redesign.db20}${imp};
      background: ${({ theme }) => theme.colors.white}${imp};
    }
    &:hover {
      color: ${({ theme }) => theme.colors.redesign.b100}${imp};
      background: ${props => props.theme.colors.redesign.b10}${imp};
    }
    &:active:enabled {
      border-color: ${({ theme }) => theme.colors.redesign.b100}${imp};
    }
    &:disabled {
      background: ${({ theme }) => theme.colors.redesign.db5}${imp};
      color: ${({ theme }) => theme.colors.redesign.db40}${imp};
      pointer-events: none;
    }

    &.${$activeClass ?? "active"} {
      background: ${({ theme }) => theme.colors.redesign.b10}${imp};
      color: ${({ theme }) => theme.colors.redesign.b100}${imp};
      border-color: ${({ theme }) => theme.colors.redesign.b100}${imp};
    }

    &.danger {
      &:hover {
        background: ${({ theme }) => theme.colors.redesign.r10}${imp};
        border-color: ${({ theme }) => theme.colors.redesign.r100}${imp};
        color: ${({ theme }) => theme.colors.redesign.r100}${imp};
      }
    }
  `;
};

export const styles: Record<Variant, RuleSet> = {
  action: css`
    padding: 12px 16px;
    border-radius: 8px;

    ${getActionButtonColorStyles()}
  `,
  danger: css`
    ${baseStyles}
    background: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.redesign.r100};
    color: ${({ theme }) => theme.colors.redesign.r100};

    &:active:enabled,
    &:hover {
      background: ${({ theme }) => theme.colors.redesign.r100};
      color: ${({ theme }) => theme.colors.white};
    }
  `,

  primary: css`
    ${baseStyles}
    background: ${({ theme }) => theme.colors.redesign.b100};
    color: ${({ theme }) => theme.colors.white};

    &:active:enabled,
    &:hover {
      background: ${props => props.theme.colors.redesign.b70};
    }
    &:disabled {
      background: ${({ theme }) => theme.colors.redesign.db10};
      color: ${({ theme }) => theme.colors.redesign.db50};
    }
  `,

  secondary: css`
    ${baseStyles}
    background: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.redesign.b100};
    color: ${({ theme }) => theme.colors.redesign.b100};
    flex-shrink: 1;

    &:active:enabled,
    &:hover:not(:disabled) {
      background: ${props => props.theme.colors.redesign.b70};
      color: ${props => props.theme.colors.white};
    }
    &:disabled {
      border-color: ${({ theme }) => theme.colors.redesign.db10};
      color: ${({ theme }) => theme.colors.redesign.db50};
    }
  `,

  text: css`
    ${baseStyles}
    background: transparent;
    border: none;
    color: ${({ theme }) => theme.colors.redesign.b100};
    flex-shrink: 1;

    &:active:enabled,
    &:hover {
      background: transparent;
      opacity: 0.7;
    }
  `,

  secondaryLink: css`
    ${baseStyles}
    padding: 0;
    background: transparent;
    border: none;
    color: ${({ theme }) => theme.colors.redesign.db60};
    text-decoration: underline;
    flex-shrink: 1;
    min-height: auto;
    flex-basis: auto;

    &:active:enabled,
    &:hover {
      background: transparent;
      opacity: 0.7;
    }
  `,

  tertiary: css`
    ${baseStyles}
    background: "transparent";
    color: ${({ theme }) => theme.colors.primary.base};
    border: 1px solid ${({ theme }) => theme.colors.redesign.db30};

    &:active:enabled,
    &:hover {
      opacity: 0.7;
    }
    &:disabled {
      color: ${({ theme }) => theme.colors.redesign.db50};
    }
  `,

  tertiaryBlue: css`
    background: "transparent";
    color: ${({ theme }) => theme.colors.redesign.b100};
    font-size: 16px;

    &:active:enabled,
    &:hover {
      color: ${props => props.theme.colors.redesign.b70};
    }
    &:disabled {
      color: ${({ theme }) => theme.colors.redesign.db50};
    }
  `,
};

const StyledButton = styled.button<{
  $active: boolean;
  $fullWidth?: boolean;
  $size?: Size;
  $small: boolean;
  $uppercase: boolean;
  $variant: Variant;
}>`
  all: unset;
  outline: revert;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: pointer;
  user-select: none;
  line-height: 14px;
  letter-spacing: 0;
  font-weight: 500;
  font-size: 14px;
  white-space: nowrap;
  text-align: center;
  transition: 0.2s;
  gap: 8px;

  &:disabled {
    cursor: not-allowed;
  }

  ${({ $variant }) => styles[$variant]}
  ${({ $active }) =>
    $active &&
    css`
      background: ${props => props.theme.colors.redesign.b100};
      color: ${props => props.theme.colors.white};

      &:hover {
        background: ${props => props.theme.colors.redesign.b100};
        color: ${props => props.theme.colors.white};
      }
    `}
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}
  ${({ $uppercase }) =>
    $uppercase &&
    css`
      text-transform: uppercase;
    `}
  ${({ $small }) =>
    $small &&
    css`
      min-height: 32px;
      max-height: 32px;
      padding: 0 12px;
    `}
  ${({ $size }) =>
    $size === "large" &&
    css`
      min-height: 40px;
      max-height: 40px;
      padding: 0 24px;
      text-transform: none;
    `}
  ${({ $size }) =>
    $size === "small" &&
    css`
      min-height: 32px;
      max-height: 32px;
      padding: 0 8px;
      line-height: 16px;
      font-weight: 500;
      gap: 6px;
    `}

  &.square {
    border-radius: 8px;
  }
`;
